<template>
	<div class="progress-bar-conatiner" :type="type" :size="size" :mode="mode">
		<div v-if="label" class="progress-bar-label">
			{{ label }}
		</div>
		<div class="progress-bar-wrapper" :shape="shape">
			<div class="progress-bar" :style="{ transform: 'scaleX(' + scaleX + ')' }" />
			<div class="progress-bar-status">
				<slot v-if="size !== 'small'" />
			</div>
			<div
				v-for="line in lines"
				:key="line.value"
				:type="line.type"
				:style="{ left: line.value + '%' }"
				class="progress-bar-vert-line"
			/>
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";

export default defineComponent({
	name: "ProgressBar",
	props: {
		/**
		 * 'normal' | 'success' | 'error' | 'warning' | 'inProgress' | 'disabled'
		 */
		type: {
			type: String,
			default: "success",
			validator(value) {
				return ["success", "normal", "error", "warning", "inProgress", "disabled"].includes(value);
			}
		},
		/**
		 * (Optional) Label is displayed on top of progress bar
		 */
		// eslint-disable-next-line vue/require-default-prop
		label: {
			type: String
		},
		/**
		 * 'x-small' | 'small' | 'medium' | 'large'
		 */
		size: {
			type: String,
			default: "medium",
			validator(value) {
				return ["xx-small", "x-small", "small", "medium", "large"].includes(value);
			}
		},
		/**
		 * Range [0 - 100]
		 * <br/> Used to showcase progress
		 */
		progress: {
			type: Number,
			default: 0.1,
			validator(value) {
				return value >= 0 && value <= 100;
			}
		},
		/**
		 * 'overlay' |  'filled'
		 */
		mode: {
			type: String,
			default: "overlay",
			validator(value) {
				return ["overlay", "filled"].includes(value);
			}
		},
		/**
		 * vertical threshold lines to display
		 *
		 * <code>
		 * <pre>
		 * [
		 * 		{
		 * 			value: Number [1-100]
		 * 			type : String 'normal' | 'success' | 'error' | 'warning' | 'inProgress' | 'disabled'
		 * 		}
		 * ]
		 * </pre>
		 * </code>
		 *
		 */
		lines: {
			type: Array,
			default: () => []
		},
		/**
		 * 'rounded' | 'box'
		 */
		shape: {
			type: String,
			default: "rounded",
			validator(value) {
				return ["rounded", "box"].includes(value);
			}
		}
	},
	computed: {
		// a computed getter
		scaleX() {
			let val = this.progress / 100;
			if (this.type === "disabled") {
				val = 0;
			}
			// `this` points to the vm instance
			return val;
		}
	}
});
</script>
<style lang="scss">
@use "sass:map";
@use "sass:math";

$progress-bar-min-width: 100px;
$progress-status-text-color: #fff; // it is always white in any theme
$progress-label-text-color: var(--dashboard-font-dark);
$progress-label-disabled-text-color: var(--dashboard-font-light);
$filled-mode-base: var(--dashboard-element-light);

$progress-bar-types: "normal", "success", "error", "warning", "inProgress", "disabled";
$progress-bar-colors: (
	"success": var(--dashboard-success),
	"error": var(--dashboard-danger),
	"warning": var(--dashboard-warning),
	"inProgress": var(--dashboard-primary),
	"normal": var(--dashboard-border),
	"disabled": var(--dashboard-border-light)
);
$progress-bar-sizes: "xx-small", "x-small", "small", "medium", "large";
$progress-bar-heights: (
	"xx-small": 3px,
	"x-small": 6px,
	"small": 10px,
	"medium": 24px,
	"large": 48px
);
$progress-bar-label-font-size: (
	"small": 8px,
	"medium": 10px,
	"large": 16px
);

.progress-bar-conatiner {
	display: flex;
	flex-direction: column;

	@each $type in $progress-bar-types {
		&[type="#{$type}"][mode="overlay"] {
			> .progress-bar-wrapper {
				background-color: map-get($progress-bar-colors, $type) !important;
			}
		}
		&[type="#{$type}"][mode="filled"] {
			> .progress-bar-wrapper {
				> .progress-bar {
					background-color: map-get($progress-bar-colors, $type);
					opacity: 1;
				}
			}
		}
	}
	&[mode="filled"] {
		> .progress-bar-wrapper {
			background-color: $filled-mode-base;
		}
	}

	&[type="disabled"] {
		> .progress-bar-label {
			color: $progress-label-disabled-text-color !important;
		}
	}

	@each $size in $progress-bar-sizes {
		&[size="#{$size}"] {
			> .progress-bar-label {
				font-size: map-get($progress-bar-label-font-size, $size);
				margin-bottom: 8px;
				font-weight: 500;
				text-transform: uppercase;
				color: $progress-label-text-color;
				// word-break: break-all;
				line-height: 10px;

				text-overflow: ellipsis;
				white-space: nowrap;
				overflow: hidden;
			}
			> .progress-bar-wrapper {
				height: map-get($progress-bar-heights, $size);
				border-radius: (math.div(map-get($progress-bar-heights, $size), 2px)) + px;

				&[shape="box"] {
					border-radius: 0px;
				}
			}
		}
	}
	.progress-bar-wrapper {
		min-width: $progress-bar-min-width;
		position: relative;
		overflow: hidden;
		> .progress-bar {
			height: inherit;
			background-color: #000;
			opacity: 0.2;
			border-top-left-radius: inherit;
			border-bottom-left-radius: inherit;
			transform-origin: 0% 0%;
			transition: transform 1s linear;
		}
		> .per-100 {
			border-top-right-radius: inherit;
			border-bottom-right-radius: inherit;
		}
		> .progress-bar-status {
			color: $progress-status-text-color;
			position: absolute;
			top: 0;
			display: flex;
			padding-left: 10px;
			align-items: center;
			bottom: 0;
			letter-spacing: 3px;
			font-size: 12px;
			font-weight: 500;
		}

		// displaying vertical threshold line
		> .progress-bar-vert-line {
			height: inherit;
			width: 1px;
			border-left: 1px solid var(--dashboard-font-dark);
			position: absolute;
			top: 0px;
			@each $type in $progress-bar-types {
				&[type="#{$type}"] {
					border-left: 2px solid map-get($progress-bar-colors, $type);
				}
			}
		}
	}
}
</style>
